import { useState, useCallback, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  IconButton,
  Box,
  Stack,
  Typography,
  CircularProgress,
  Checkbox,
} from "@mui/material";
import { Close, Add, Group } from "@mui/icons-material";
import { enqueueSnackbar } from "notistack";
// config
import colors from "../../../config/colors";
// components
import CreateModal from "../create-wishlist/CreateModal";
// hooks
import { useInfiniteScroll } from "../../../hooks/useInfiniteScroll";
// context
import { useAuth } from "../../../context/AuthContext";
// redux
import { useSelector, useDispatch } from "../../../redux/store";
import { setWishlistCreatorId } from "../../../redux/slices/creator";
import {
  useLazyGetBrandWishlistQuery,
  useAddCreatorToWishlistMutation,
} from "../../../redux/apiSlices/wishlist";
// ga
import {
  triggerGa,
  CATEGORY,
  ACTION,
  PAGE,
  SCREEN,
  LABEL,
  LABEL2,
} from "../../../utils/gaUtils";

const AddToWishlistModal = () => {
  const { user, selectedEmailUserBrand } = useAuth();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [selectedWishlists, setSelectedWishlists] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [wishlist, setWishlist] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [isInitialLoading, setIsInitialLoading] = useState(false);

  const { wishlistCreatorId } = useSelector((state) => state.creator);

  // Admin detection for Discovery page
  const isAdmin = user?.data?.user_type === "adminUser";
  const selectedBrandId = isAdmin ? user?.data?.parent_brand?.id : null;

  const [fetchWishlist, { isFetching }] = useLazyGetBrandWishlistQuery();

  const hasMore = pagination?.has_next || false;

  const loadWishlistData = useCallback(
    async (page, creatorId) => {
      // For admin users, we might not have user.data.id, so check differently
      if (!isAdmin && !user?.data?.id) {
        return;
      }

      if (isAdmin && !selectedBrandId) {
        return;
      }

      try {
        setIsInitialLoading(page === 1);
        // Prepare request body - for admin users, add admin_brand_id to get admin-created wishlists
        const requestBody = {
          wishlink_username: creatorId,
          pagination: {
            page,
            rows_per_page: 10,
          },
        };

        // For admin users, add admin_brand_id to get admin-created wishlists (user_id = null)
        if (isAdmin && selectedBrandId) {
          requestBody.admin_brand_id = selectedBrandId;
        } else {
          requestBody.brand_id = selectedEmailUserBrand?.id;
        }

        const result = await fetchWishlist(requestBody).unwrap();

        if (page === 1) {
          setWishlist(result.data || []);
        } else {
          setWishlist((prev) => [...prev, ...(result.data || [])]);
        }

        setPagination(result.pagination);
        setIsInitialLoading(false);
      } catch (error) {
        console.error("Error loading wishlist:", error);
        setIsInitialLoading(false);
      }
    },
    [
      user?.data?.id,
      fetchWishlist,
      isAdmin,
      selectedBrandId,
      selectedEmailUserBrand,
      wishlistCreatorId,
    ],
  );

  const loadMore = useCallback(() => {
    if (hasMore && !isFetching) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      loadWishlistData(nextPage, wishlistCreatorId);
    }
  }, [hasMore, isFetching, currentPage, loadWishlistData]);

  const containerRef = useInfiniteScroll(loadMore, hasMore, isFetching);

  useEffect(() => {
    // For admin users, we don't need user.data.id, just selectedBrandId
    const shouldLoad =
      wishlistCreatorId &&
      ((isAdmin && selectedBrandId) || (!isAdmin && user?.data?.id));

    if (shouldLoad) {
      setCurrentPage(1);
      loadWishlistData(1, wishlistCreatorId);
    }
  }, [
    wishlistCreatorId,
    user?.data?.id,
    isAdmin,
    selectedBrandId,
    loadWishlistData,
  ]);

  const [addCreatorToWishlist, { isLoading: isAddCreatorToWishlistLoading }] =
    useAddCreatorToWishlistMutation();

  const handleClose = () => {
    setSelectedWishlists([]);
    setWishlist([]);
    setPagination(null);
    setCurrentPage(1);
    setIsInitialLoading(false);
    dispatch(setWishlistCreatorId(null));
  };

  const handleCreateWishlist = () => {
    triggerGa({
      category: CATEGORY.CREATOR_DISCOVERY,
      action: ACTION.BUTTON_CLICK,
      label: LABEL.CREATE_NEW_WISHLIST,
      page: PAGE.CREATORS,
      screenName: SCREEN.DISCOVER,
    });
    setOpen(true);
  };

  const handleCloseCreateWishlist = (wishlistId) => {
    loadWishlistData(1, wishlistCreatorId);
    if (wishlistId) {
      handleWishlistToggle(wishlistId);
    }
    setOpen(false);
  };

  const handleWishlistToggle = (wishlistId) => {
    setSelectedWishlists((prev) => {
      if (prev.includes(wishlistId)) {
        return prev.filter((id) => id !== wishlistId);
      } else {
        return [...prev, wishlistId];
      }
    });
  };

  const handleSubmit = () => {
    addCreatorToWishlist({
      creatorId: wishlistCreatorId,
      body: {
        wishlist_ids: selectedWishlists,
      },
    })
      .unwrap()
      .then(() => {
        handleClose();
        triggerGa({
          category: CATEGORY.CREATOR_DISCOVERY,
          action: ACTION.BUTTON_CLICK,
          label: LABEL.ADD_TO_WISHLIST,
          label2: LABEL2.ADDED_TO_EXISTING,
          label3: wishlistCreatorId,
          page: PAGE.CREATORS,
          screenName: SCREEN.DISCOVER,
        });
        enqueueSnackbar(`Creator added to wishlist!`, {
          variant: "success",
        });
      })
      .catch((err) => {
        console.log(err);
        enqueueSnackbar(`Failed to add creator to wishlist!`, {
          variant: "error",
        });
      });
  };

  return (
    <Dialog
      open={!!wishlistCreatorId}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "8px",
          padding: "24px",
          boxShadow: "0 2px 4px 2px rgba(0, 0, 0, 0.25)",
        },
      }}
    >
      <DialogTitle sx={{ p: 0, mb: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography fontSize={20} fontWeight={600}>
            Add to Wishlist
          </Typography>
          <IconButton
            onClick={handleClose}
            size="small"
            data-cy="add-to-wishlist-close-button"
          >
            <Close sx={{ color: colors.black }} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Stack spacing={2}>
          <Box
            ref={containerRef}
            sx={{
              maxHeight: "300px",
              overflowY: "auto",
              pr: 1,
              "&::-webkit-scrollbar": {
                width: "4px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: colors.gray100,
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: colors.primary500,
                borderRadius: "2px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                backgroundColor: colors.primary500,
                opacity: 0.8,
              },
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              sx={{
                backgroundColor: colors.primary100,
                borderRadius: "4px",
                padding: "12px",
                gap: "8px",
                cursor: "pointer",
              }}
              mb={"12px"}
              onClick={handleCreateWishlist}
            >
              <Add />
              <Typography fontSize={16} fontWeight={500}>
                Create New Wishlist
              </Typography>
            </Stack>
            <Stack spacing={"12px"}>
              {isInitialLoading ? (
                <Box display="flex" justifyContent="center" p={2}>
                  <CircularProgress size={24} />
                </Box>
              ) : wishlist?.length === 0 ? (
                <Typography
                  textAlign="center"
                  color={colors.gray200}
                  fontSize={14}
                  py={2}
                >
                  No wishlists found. Create your first wishlist!
                </Typography>
              ) : (
                <>
                  {wishlist?.map((wishlistItem) => (
                    <Stack
                      key={wishlistItem.id}
                      flexDirection="row"
                      alignItems="center"
                      sx={{
                        padding: "12px",
                        borderRadius: "4px",
                        border: `1px solid ${colors.gray100}`,
                        cursor: wishlistItem?.creator_exists
                          ? "not-allowed"
                          : "pointer",
                        opacity: wishlistItem?.creator_exists ? 0.6 : 1,
                        backgroundColor: wishlistItem?.creator_exists
                          ? colors.gray100
                          : "transparent",
                        ...(!wishlistItem?.creator_exists && {
                          "&:hover": {
                            borderColor: colors.primary500,
                            backgroundColor: colors.primary100,
                          },
                        }),
                      }}
                      onClick={() =>
                        !wishlistItem?.creator_exists &&
                        handleWishlistToggle(wishlistItem.id)
                      }
                    >
                      <Checkbox
                        checked={selectedWishlists.includes(wishlistItem.id)}
                        onChange={() => handleWishlistToggle(wishlistItem.id)}
                        disabled={wishlistItem?.creator_exists}
                        sx={{
                          color: colors.gray200,
                          "&.Mui-checked": {
                            color: colors.primary500,
                          },
                          "&.Mui-disabled": {
                            color: colors.gray200,
                          },
                          padding: "4px",
                          marginTop: "-4px",
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <Stack sx={{ gap: "8px", flex: 1, marginLeft: "8px" }}>
                        <Stack flexDirection="row" alignItems="center" gap={2}>
                          <Typography
                            fontSize={16}
                            fontWeight={500}
                            color={
                              wishlistItem?.creator_exists
                                ? colors.gray200
                                : "inherit"
                            }
                          >
                            {wishlistItem?.name}
                          </Typography>
                          <Stack
                            flexDirection="row"
                            alignItems="center"
                            gap={1}
                            bgcolor={
                              wishlistItem?.creator_exists
                                ? colors.gray100
                                : colors.primary100
                            }
                            borderRadius="4px"
                            padding="4px 8px"
                          >
                            <Group fontSize="10px" />
                            <Typography fontSize={10} fontWeight={500}>
                              {wishlistItem?.creator_count || 0}
                            </Typography>
                          </Stack>
                          {wishlistItem?.creator_exists && (
                            <Typography
                              fontSize={10}
                              fontWeight={500}
                              color={colors.gray200}
                              bgcolor={colors.white}
                              borderRadius="4px"
                              padding="2px 6px"
                              border={`1px solid ${colors.gray100}`}
                            >
                              Already exists
                            </Typography>
                          )}
                        </Stack>
                        {wishlistItem?.description && (
                          <Typography fontSize={14} color={colors.gray200}>
                            {wishlistItem.description}
                          </Typography>
                        )}
                      </Stack>
                    </Stack>
                  ))}
                  {isFetching && !isInitialLoading && (
                    <Box display="flex" justifyContent="center" p={2}>
                      <CircularProgress size={20} />
                    </Box>
                  )}
                </>
              )}
            </Stack>
          </Box>

          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={
              isAddCreatorToWishlistLoading || selectedWishlists.length === 0
            }
            sx={{
              backgroundColor: colors.primary500,
              color: "white",
              display: "flex",
              padding: "12px",
              justifyContent: "center",
              alignItems: "center",
              borderRadius: "8px",
              textTransform: "none",
              fontSize: "14px",
              fontWeight: 500,
              "&.Mui-disabled": {
                backgroundColor: colors.gray100,
                color: colors.gray200,
              },
            }}
          >
            {isAddCreatorToWishlistLoading ? (
              <>
                <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
                Adding...
              </>
            ) : (
              "Add to Wishlist"
            )}
          </Button>
        </Stack>
      </DialogContent>
      <CreateModal
        open={open}
        onClose={handleCloseCreateWishlist}
        isAdmin={isAdmin}
        selectedBrandId={selectedBrandId}
      />
    </Dialog>
  );
};

export default AddToWishlistModal;
